"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { TRANSFORMER_MAX_MOBILE_WIDTH_PX } from "../../../utils/constants.js";
import { mergeRefs } from "../../../utils/mergeRefs.js";
import { InputButton } from "../../InputButton/index.js";
import { Footer } from "../CustomComponents/Footer.js";
import { getDefaultMonth, getFormattedDates, shouldAutoClose } from "../helpers/helpers.js";
import { DatePickerDesktop } from "./DatePickerDesktop.js";
import { DatePickerMobile } from "./DatePickerMobile.js";
const DEFAULT_DATE_PICKER_FORMAT = 'ccc, MMM d';
function DatePickerWrapper({ mode = 'single', handleCalendarChange, footerLeftNode, subHeading, components: initialComponents, label, selected, displayNights, isOpen: isCalendarOpen = false, customDateFormat, onMonthChange: onMonthChangeProp, defaultMonth: defaultMonthProp, placeholder, isDisabled, errorMessage, helperText, errorText, inputRef, ...restProps }) {
    const [isOpen, setIsOpen] = useState(isCalendarOpen);
    const showDrawerOverlay = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const initialFormattedDates = useMemo(()=>{
        if (subHeading) return subHeading;
        if (selected) return getFormattedDates({
            dates: selected,
            dateFormat: DEFAULT_DATE_PICKER_FORMAT,
            displayNights
        });
        return '';
    }, [
        selected,
        subHeading,
        displayNights
    ]);
    const [calendarState, setCalendarState] = useState({
        selectedDates: selected,
        defaultMonth: getDefaultMonth({
            mode,
            selected
        }) ?? defaultMonthProp,
        formattedDates: initialFormattedDates
    });
    const handleCalendarDates = useCallback((newDates, triggerDate)=>{
        setCalendarState((prev)=>({
                ...prev,
                selectedDates: newDates,
                defaultMonth: getDefaultMonth({
                    mode,
                    selected: newDates
                }) ?? defaultMonthProp,
                formattedDates: getFormattedDates({
                    dates: newDates,
                    dateFormat: customDateFormat ?? DEFAULT_DATE_PICKER_FORMAT,
                    displayNights
                })
            }));
        handleCalendarChange(newDates, triggerDate);
        if (shouldAutoClose(mode, newDates)) setIsOpen(false);
    }, [
        mode,
        defaultMonthProp,
        customDateFormat,
        displayNights,
        handleCalendarChange
    ]);
    const onMonthChange = useCallback((month)=>{
        onMonthChangeProp?.(month);
    }, [
        onMonthChangeProp
    ]);
    const handleCalendarDisplayState = useCallback((val)=>{
        setIsOpen(val);
    }, []);
    const handleOpen = useCallback(()=>{
        setIsOpen(true);
    }, []);
    const value = useMemo(()=>subHeading || calendarState.formattedDates || '', [
        subHeading,
        calendarState.formattedDates
    ]);
    const subHeadingValue = useMemo(()=>subHeading || calendarState.formattedDates || label || '', [
        subHeading,
        calendarState.formattedDates,
        label
    ]);
    const InputTrigger = useCallback(({ onClick, ref, ...props })=>/*#__PURE__*/ jsx(InputButton, {
            ...props,
            ref: mergeRefs(ref, inputRef),
            "data-testid": "date-picker-input",
            className: "w-full",
            label: label,
            value: value,
            iconLeft: 'calendar_month',
            size: 'lg',
            placeholder: placeholder,
            isDisabled: isDisabled,
            errorMessage: errorMessage,
            helperText: helperText,
            errorText: errorText,
            onClick: onClick
        }), [
        errorMessage,
        errorText,
        helperText,
        isDisabled,
        inputRef,
        label,
        placeholder,
        value
    ]);
    const components = useMemo(()=>({
            ...initialComponents,
            Footer: ()=>/*#__PURE__*/ jsx(Footer, {
                    footerLeftNode: footerLeftNode,
                    handleFooterCallBack: handleCalendarDisplayState
                })
        }), [
        initialComponents,
        footerLeftNode,
        handleCalendarDisplayState
    ]);
    const { selectedDates, defaultMonth } = calendarState;
    const props = {
        ...restProps,
        mode,
        open: isOpen,
        handleCalendarChange: handleCalendarDates,
        selected: selectedDates,
        onMonthChange,
        defaultMonth
    };
    return showDrawerOverlay ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            InputTrigger({
                onClick: handleOpen
            }),
            /*#__PURE__*/ jsx(DatePickerMobile, {
                ...props,
                isMobile: true,
                handleDrawerState: handleCalendarDisplayState,
                subHeading: subHeadingValue,
                autoFocus: true
            })
        ]
    }) : /*#__PURE__*/ jsx(DatePickerDesktop, {
        ...props,
        handlePopOverOpenState: handleCalendarDisplayState,
        inputNode: InputTrigger,
        title: label,
        components: components
    });
}
const DatePicker = /*#__PURE__*/ memo(DatePickerWrapper);
export { DEFAULT_DATE_PICKER_FORMAT, DatePicker };
